import { useEffect, useMemo, useState } from "react";
import ButtonIcon from "components/utility/button/ButtonIcon";
import ButtonIconDownload from "components/utility/button/ButtonIconDownload";
import ContentPageActionsShareModal from "./ContentPageActionsShareModal";
import { isMobile as checkIfIsMobile } from "components/utility/page-responsive/PageResponsive";
import BodyMask from "components/utility/body-mask/BodyMask";
import TooltipButton from "components/utility/tooltip/TooltipButton";
import { v1 as uuidv1 } from "uuid";
import { extractNameFromPageTitle } from "components/utility/analytics/util";

interface ContentPageActionsContentProps {
	data: {
		isHeaderCollapsed: "true" | "false";
		share;
	};
	dataset: {
		hideShare: "true" | "false";
		hideFavorite: "true" | "false";
		hideDownload: "true" | "false";
		isCollectionPageTop: "true" | "false";
		isSupportingAsset: "true" | "false";
		color: "teal" | "default" | "yellow";
		downloadUrl: string;
		isModule: string;
		isContentPageTop: string;
		isHeaderCollapsed: string;
		title: string;
	};
	isCollection: boolean;
}

const ContentPageActionsContent = ({
	data,
	dataset,
	isCollection
}: React.PropsWithChildren<ContentPageActionsContentProps>) => {
	const { share } = data ?? {};
	const { isDisable } = share ?? "";
	const {
		isContentPageTop = "false",
		isCollectionPageTop = "false",
		title,
		isHeaderCollapsed,
		isModule = "false",
		isSupportingAsset,
		color,
		hideDownload,
		hideFavorite,
		downloadUrl = "",
		hideShare
	} = dataset;
	let className = useMemo(
		() => (isCollection ? "collection-page" : "content-page"),
		[isCollection]
	);

	const [isLocked, setIsLocked] = useState<boolean>(false);
	const [isMobile, setIsMobile] = useState<boolean>(false);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [toggle, setToggle] = useState<boolean>(false);
	const [closeButton, setCloseButton] = useState<boolean>(false);

	const handleWindowClick = (e) => {
		if (!e || !e.target) return;
		let currentElement = e.target;

		while (currentElement) {
			if (currentElement.classList.contains("info-popup-content")) return;
			currentElement = currentElement.parentElement;
		}

		setIsLocked(false);

		window.removeEventListener("click", handleWindowClick);
	};

	const toggleLocked = () => {
		setIsLocked(!isLocked);

		if (!isDisable) {
			setIsModalOpen(!isModalOpen);
			if (isMobile) {
				setToggle(false);
			}
		}

		window.requestAnimationFrame(() => {
			if (isLocked) {
				window.addEventListener("click", handleWindowClick);
			} else {
				window.removeEventListener("click", handleWindowClick);
			}
		});
	};

	const setOpenFlag = (flag) => {
		setIsModalOpen(flag);
	};

	const closeModal = (flag) => {
		setTimeout(function () {
			setIsModalOpen(!flag);
		}, 2000);
	};

	const getButtons = () => {
		const { isDisable, metaData } = data?.share ?? {};

		let buttons: JSX.Element[] = [];
		let isTop = isContentPageTop === "true" || isCollectionPageTop === "true" ? true : false;
		const isHeaderCollapsed = dataset.isHeaderCollapsed === "true" ? true : false;

		if (hideFavorite.toLowerCase() !== "true") {
			buttons.push(
				<ButtonIcon
					type="favorite"
					key="favorite"
					onClick={() => openModal("favorite")}
					color={color || "default"}
					className={isCollection ? " collection" : ""}
				/>
			);
		}

		if (hideShare.toLowerCase() !== "true") {
			let className = isDisable ? `button-icon-${color}-disable` : "";
			buttons.push(
				<div
					className={
						"button-icon-share-wrapper" + (isCollectionPageTop === "true" ? " collection" : "")
					}
					key="share"
				>
					{metaData && isDisable ? (
						<TooltipButton
							id={uuidv1()}
							content={metaData}
							position="bottom"
							tooltipPosition={isTop ? "right" : "left"}
							clickable={true}
						>
							<ButtonIcon
								type="share"
								key="share"
								color={color || "default"}
								className={className + (isCollection ? " collection" : "")}
								isButton={false}
								onClick={() => null}
							/>
						</TooltipButton>
					) : (
						<ButtonIcon
							type="share"
							key="share"
							onClick={() => openModal("share")}
							color={color || "default"}
							className={className + (isCollection ? " collection" : "")}
							dataAEvt="Share Initiated"
							dataAComp={
								isCollection
									? "Utility Card"
									: isHeaderCollapsed
										? "Utility Navigation"
										: "Article Header"
							}
							dataAName={extractNameFromPageTitle(title)}
						/>
					)}
				</div>
			);
		}

		if (hideDownload.toLowerCase() !== "true") {
			buttons.push(
				<ButtonIconDownload
					downloadUrl={downloadUrl}
					key="download"
					color={color || "default"}
					dataA="true"
					dataAEvt={"Download"}
					dataAName={extractNameFromPageTitle(title)}
					onClick={() => null}
					dataAComp={
						isCollection
							? "Utility Card"
							: isSupportingAsset === "true"
								? "Supporting Materials"
								: isHeaderCollapsed
									? "Utility Nav"
									: "Article Header"
					}
					dataQASuffix={downloadUrl ? "url" : "pdf-convertor"}
				/>
			);
		}

		return buttons;
	};

	const getMobileButtons = () => {
		let more: JSX.Element[] = [];
		let isHidden = false;
		if (
			hideFavorite.toLowerCase() === "true" &&
			hideDownload.toLowerCase() === "true" &&
			hideShare.toLowerCase() === "true"
		) {
			isHidden = true;
		}
		if (isModule === "true" && !isHidden) {
			more.push(
				<ButtonIcon
					type="more"
					key="more"
					onClick={() => openModal("more")}
					color={color || "default"}
					className={isCollection ? " collection" : ""}
				/>
			);
		}
		return more;
	};

	const keyHandler = (e) => {
		const key = e.which || e.keyCode || 0;
		if (key === 13 || key === 32) {
			openModal("mask");
		}
	};

	const handleCloseButton = (flag) => {
		setCloseButton(flag);
		if (!isDisable && isMobile && closeButton) {
			setToggle(false);
			setTimeout(function () {
				setIsModalOpen(false);
			}, 2000);
		}
	};

	const openModal = (type) => {
		if (type === "mask" || type === "more") {
			setToggle(!toggle);
		} else if (type === "share") {
			toggleLocked();
		}
	};

	useEffect(() => {
		const onResize = () => {
			setIsMobile(checkIfIsMobile());
		};

		window.addEventListener("load", onResize);
		window.addEventListener("resize", onResize);

		return () => {
			window.removeEventListener("load", onResize);
			window.removeEventListener("resize", onResize);
		};
	}, []);

	if (isContentPageTop === "true" || isCollectionPageTop === "true") {
		return (
			<div className={`${className}-actions`}>
				<BodyMask
					className={toggle ? "body-mask" : ""}
					onClick={() => openModal("mask")}
					onKeyUp={(e) => keyHandler(e)}
				/>
				<div
					className={
						`${className}-actions-normal-top` + (toggle ? ` ${className}-actions-mobile` : "")
					}
				>
					{getButtons()}
				</div>
				<div className={`${className}-actions-more`}>{getMobileButtons()}</div>
				{!isDisable && isModalOpen ? (
					<ContentPageActionsShareModal
						isOpen={isModalOpen}
						setOpenFlag={setOpenFlag}
						data={share}
						modalClick={handleCloseButton}
						isCopied={closeModal}
						analytics={{
							comp: isHeaderCollapsed === "true" ? "Utility Navigation" : "Article Header"
						}}
					/>
				) : null}
			</div>
		);
	} else {
		return (
			<div className={`${className}-actions`}>
				<BodyMask
					className={toggle ? "body-mask" : ""}
					onClick={() => openModal("mask")}
					onKeyUp={(e) => keyHandler(e)}
				/>

				<div
					className={`${className}-actions-normal` + (toggle ? ` ${className}-actions-mobile` : "")}
				>
					{getButtons()}
				</div>
				<div className={`${className}-actions-more`}>{getMobileButtons()}</div>
				{!isDisable && isModalOpen ? (
					<ContentPageActionsShareModal
						isOpen={isModalOpen}
						setOpenFlag={setOpenFlag}
						data={share}
						modalClick={handleCloseButton}
						isCopied={closeModal}
						analytics={{
							title,
							comp: "Utility Card"
						}}
					/>
				) : null}
			</div>
		);
	}
};
export default ContentPageActionsContent;
